import * as React from 'react'
import { Eye, FlaskConical, Loader2, RefreshCw, TriangleAlert } from 'lucide-react'
import type { ModeSettingsDto, TenantProfileDto } from '@lib/dto/tenant-settings.dto'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import {
  updateModeSettingsInputSchema,
  WEBHOOK_SECRET_OVERLAPS,
  webhookSecretOverlapSchema,
  type WebhookSecretOverlap,
} from '@lib/input/tenant-settings.input'
import { SecretValue } from '@/components/keys/SecretValue'
import { McpSettingCard } from '@/components/mcp/McpSettingCard'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState, LoadingState } from '@/components/PageState'
import { BrandingSummary } from '@/components/tenants/BrandingSummary'
import { TenantForm } from '@/components/tenants/TenantForm'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { Tooltip } from '@/components/ui/tooltip'
import {
  canEditProfile,
  canEditSettings,
  canManageTeam,
  canSeeSecrets,
  useDashboardScope,
} from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage, apiFieldErrors } from '@/helpers/apiErrorMessage'
import { useMcpEnabled } from '@/hooks/useConnectedApps'
import { useSettingsActions, useTenantProfile, useTenantSettings } from '@/hooks/useTenantSettings'

type Actions = ReturnType<typeof useSettingsActions>


/** Whether the saved webhook URL has accepted a webhook (a delivery or test event answered 2xx). */
function WebhookVerification({ settings }: { settings: ModeSettingsDto }) {
  if (!settings.webhookUrl) return null
  if (!settings.webhookVerifiedAt) {
    return <span className="text-caption text-muted-foreground">Not verified yet</span>
  }
  return (
    <Tooltip content={`Last accepted a webhook ${new Date(settings.webhookVerifiedAt).toLocaleString()}`}>
      <Badge tone="success">Verified</Badge>
    </Tooltip>
  )
}

interface IntegrationErrors {
  webhookUrl?: string
  embedOrigins?: string
}

interface ModeCardProps {
  settings: ModeSettingsDto
  canEdit: boolean
  actions: Actions
}

/** The text area holds one origin per line. */
function originsFromText(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}

/** Webhook URL and embed origins for one mode, saved together. */
function IntegrationCard({ settings, canEdit, actions }: ModeCardProps) {
  const [webhookUrl, setWebhookUrl] = React.useState(settings.webhookUrl ?? '')
  const [origins, setOrigins] = React.useState(settings.embedOrigins.join('\n'))
  const [errors, setErrors] = React.useState<IntegrationErrors>({})

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const lines = originsFromText(origins)
    const parsed = updateModeSettingsInputSchema.safeParse({
      webhookUrl: webhookUrl.trim() || null,
      embedOrigins: lines,
    })
    if (!parsed.success) {
      const next: IntegrationErrors = {}
      for (const issue of parsed.error.issues) {
        const [field, index] = issue.path
        if (field === 'webhookUrl') next.webhookUrl ??= issue.message
        if (field === 'embedOrigins') {
          next.embedOrigins ??= typeof index === 'number' ? `${lines[index] ?? ''}: ${issue.message}` : issue.message
        }
      }
      setErrors(next)
      return
    }
    setErrors({})
    try {
      const saved = await actions.updateSettings.mutateAsync({ mode: settings.mode, input: parsed.data })
      setOrigins(saved.embedOrigins.join('\n'))
      toast.success('Settings saved')
    } catch (failure) {
      const fields = apiFieldErrors(failure, ['webhookUrl', 'embedOrigins'])
      if (fields) setErrors(fields)
      else toast.error(apiErrorMessage(failure, 'Could not save the settings.'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhook and embedding</CardTitle>
        <CardDescription>Used by every {settings.mode} key.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <Field
            label="Webhook URL"
            htmlFor="webhook-url"
            error={errors.webhookUrl}
            hint="PixelSign POSTs envelope and signer events here. Leave empty to stop webhooks."
            labelAside={<WebhookVerification settings={settings} />}
          >
            <Input
              id="webhook-url"
              type="url"
              placeholder="https://"
              value={webhookUrl}
              disabled={!canEdit}
              aria-invalid={errors.webhookUrl ? true : undefined}
              aria-describedby={fieldMessageId('webhook-url')}
              onChange={(event) => setWebhookUrl(event.target.value)}
            />
          </Field>
          <Field
            label="Embed origins"
            htmlFor="embed-origins"
            error={errors.embedOrigins}
            hint={
              settings.mode === 'live'
                ? 'Sites allowed to show the signing page in an iframe, one https origin per line (up to 10).'
                : 'Sites allowed to show the signing page in an iframe, one per line (up to 10). http://localhost works here.'
            }
          >
            <Textarea
              id="embed-origins"
              rows={3}
              className="font-mono text-caption"
              placeholder="https://app.example.com"
              value={origins}
              disabled={!canEdit}
              spellCheck={false}
              aria-invalid={errors.embedOrigins ? true : undefined}
              aria-describedby={fieldMessageId('embed-origins')}
              onChange={(event) => setOrigins(event.target.value)}
            />
          </Field>
          {canEdit ? (
            <div className="flex justify-end">
              <Button type="submit" disabled={actions.updateSettings.isPending}>
                {actions.updateSettings.isPending ? <Loader2 className="animate-spin" /> : null}
                Save
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  )
}

interface SecretCardProps extends ModeCardProps {
  canSee: boolean
  viewAs: boolean
}

const OVERLAP_LABELS: Record<WebhookSecretOverlap, string> = {
  '24h': '24 hours',
  '7d': '7 days',
  none: 'No overlap: switch now',
}

/**
 * Reveal and rotate the webhook signing secret. A rotation can keep the old
 * secret signing alongside the new one for a while, so the receiver can switch
 * without rejecting deliveries; the overlap can be ended early.
 */
function WebhookSecretCard({ settings, canEdit, canSee, viewAs, actions }: SecretCardProps) {
  const [secret, setSecret] = React.useState<string | null>(null)
  const [confirming, setConfirming] = React.useState(false)
  const [overlap, setOverlap] = React.useState<WebhookSecretOverlap>('24h')
  const [rotated, setRotated] = React.useState<string | null>(null)
  const [retiring, setRetiring] = React.useState(false)
  const previousUntil = settings.previousSecretExpiresAt

  async function reveal(): Promise<void> {
    try {
      setSecret((await actions.revealSecret.mutateAsync(settings.mode)).secret)
    } catch (failure) {
      toast.error(apiErrorMessage(failure, 'Could not load the secret.'))
    }
  }

  async function rotate(): Promise<void> {
    try {
      const result = await actions.rotateSecret.mutateAsync({ mode: settings.mode, input: { overlap } })
      setRotated(result.secret)
      setSecret(result.secret)
    } catch (failure) {
      toast.error(apiErrorMessage(failure, 'Could not rotate the secret.'))
      setConfirming(false)
    }
  }

  async function retirePrevious(): Promise<void> {
    try {
      await actions.retirePreviousSecret.mutateAsync(settings.mode)
      toast.success('The old secret no longer signs deliveries')
    } catch (failure) {
      toast.error(apiErrorMessage(failure, 'Could not retire the old secret.'))
    }
    setRetiring(false)
  }

  function closeDialog(): void {
    setConfirming(false)
    setRotated(null)
    setOverlap('24h')
  }

  function renderSecret() {
    if (!canSee) {
      return (
        <p className="text-body text-muted-foreground">
          {viewAs ? 'Secrets are hidden while viewing as PixelSign staff.' : 'Only owners and developers can see it.'}
        </p>
      )
    }
    if (secret) return <SecretValue value={secret} label="Webhook secret" />
    return (
      <Button variant="secondary" onClick={() => void reveal()} disabled={actions.revealSecret.isPending}>
        {actions.revealSecret.isPending ? <Loader2 className="animate-spin" /> : <Eye />}
        Reveal secret
      </Button>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhook signing secret</CardTitle>
        <CardDescription>
          Verify the <span className="font-mono">X-Pixelsign-Signature</span> header with it to know a delivery came
          from PixelSign.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {settings.hasWebhookSecret ? renderSecret() : <p className="text-body text-muted-foreground">No secret yet.</p>}
        {previousUntil ? (
          <div className="space-y-2 rounded-md border border-border bg-muted px-3 py-2">
            <p className="text-body">
              The previous secret also signs deliveries until{' '}
              <span className="font-medium">{new Date(previousUntil).toLocaleString()}</span>. Each delivery carries a
              signature for both, so a receiver with either one accepts it.
            </p>
            {canEdit ? (
              <Button variant="destructive-quiet" size="sm" onClick={() => setRetiring(true)}>
                Retire old secret now
              </Button>
            ) : null}
          </div>
        ) : null}
        {canEdit && canSee ? (
          <Button variant="destructive-quiet" onClick={() => setConfirming(true)}>
            <RefreshCw />
            Rotate secret
          </Button>
        ) : null}
      </CardContent>

      <Dialog open={confirming} onOpenChange={(open) => (open ? setConfirming(true) : closeDialog())}>
        <DialogContent>
          {rotated ? (
            <div className="space-y-4">
              <DialogHeader>
                <DialogTitle>New webhook secret</DialogTitle>
                <DialogDescription>
                  {overlap === 'none'
                    ? 'Deliveries are signed with this from now on. Update your receiver.'
                    : `Deliveries are signed with this and with the old secret for ${OVERLAP_LABELS[overlap]}. Update your receiver before then.`}
                </DialogDescription>
              </DialogHeader>
              <SecretValue value={rotated} label="Webhook secret" />
              <DialogFooter>
                <Button onClick={closeDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Rotate the {settings.mode} webhook secret?</DialogTitle>
                <DialogDescription>
                  The next delivery is signed with the new secret. During an overlap it is also signed with the current
                  one, so your receiver keeps accepting webhooks while you deploy the new secret.
                </DialogDescription>
              </DialogHeader>
              <Field
                label="Keep the current secret working for"
                htmlFor="secret-overlap"
                hint={previousUntil ? 'The secret from the rotation before this one stops signing now.' : undefined}
              >
                <Select
                  id="secret-overlap"
                  value={overlap}
                  aria-describedby={previousUntil ? fieldMessageId('secret-overlap') : undefined}
                  onChange={(event) => {
                    const next = webhookSecretOverlapSchema.safeParse(event.target.value)
                    if (next.success) setOverlap(next.data)
                  }}
                >
                  {WEBHOOK_SECRET_OVERLAPS.filter((value) => value !== 'none').map((value) => (
                    <option key={value} value={value}>
                      {OVERLAP_LABELS[value]}
                    </option>
                  ))}
                  <option value="none">{OVERLAP_LABELS.none}</option>
                </Select>
              </Field>
              {overlap === 'none' ? (
                <p className="flex items-start gap-2 text-body text-warning">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                  Until your receiver has the new secret, it will reject PixelSign&apos;s webhooks. Failed deliveries
                  are retried for up to 24 hours.
                </p>
              ) : null}
              <DialogFooter>
                <Button variant="secondary" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => void rotate()} disabled={actions.rotateSecret.isPending}>
                  {actions.rotateSecret.isPending ? <Loader2 className="animate-spin" /> : null}
                  Rotate secret
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={retiring} onOpenChange={setRetiring}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire the old {settings.mode} webhook secret?</DialogTitle>
            <DialogDescription>
              Deliveries are signed with the current secret only from now on. A receiver still using the old secret
              will reject them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRetiring(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void retirePrevious()}
              disabled={actions.retirePreviousSecret.isPending}
            >
              {actions.retirePreviousSecret.isPending ? <Loader2 className="animate-spin" /> : null}
              Retire old secret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

interface OrganizationSectionProps {
  profile: TenantProfileDto
  canEdit: boolean
  actions: Actions
}

function OrganizationSection({ profile, canEdit, actions }: OrganizationSectionProps) {
  if (!canEdit) {
    return <BrandingSummary profile={profile} description="Owners can change these." />
  }
  return (
    <TenantForm
      mode="edit"
      initial={profile}
      onSubmit={async (input) => {
        await actions.updateProfile.mutateAsync(input)
        toast.success('Organization saved')
      }}
    />
  )
}

interface SettingsSectionProps {
  title: string
  /** Which modes the section's settings apply to. */
  appliesTo: TenantMode | 'both'
  description: string
  children: React.ReactNode
}

/** A titled group of settings that says whether it follows the Live/Test switch or is shared by both. */
function SettingsSection({ title, appliesTo, description, children }: SettingsSectionProps) {
  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h2 className="text-title font-semibold">{title}</h2>
          {appliesTo === 'test' ? (
            <Badge tone="warning">
              <FlaskConical />
              Test only
            </Badge>
          ) : (
            <Badge>{appliesTo === 'live' ? 'Live only' : 'Live and test'}</Badge>
          )}
        </div>
        <p className="text-caption text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  )
}

/**
 * How PixelSign reaches the organization's systems (webhook, embedding) and
 * how signers see it (profile). Owners and developers edit the integration;
 * owners edit the profile; everyone else reads.
 */
export function SettingsPage() {
  const scope = useDashboardScope()
  const settings = useTenantSettings()
  const profile = useTenantProfile()
  const actions = useSettingsActions()
  const current = settings.data?.modes.find((row) => row.mode === scope.mode)
  const mcpEnabled = useMcpEnabled()

  function renderIntegration() {
    if (settings.isLoading) return <LoadingState />
    if (settings.isError || !settings.data) {
      return <ErrorState message={apiErrorMessage(settings.error, 'Could not load the settings.')} />
    }
    if (!current) {
      return <ErrorState message={`This organization has no ${scope.mode} settings. Contact PixelSign support.`} />
    }
    // Keyed by mode, so switching Live/Test resets the forms and any revealed secret.
    return (
      <React.Fragment key={current.mode}>
        <IntegrationCard settings={current} canEdit={canEditSettings(scope)} actions={actions} />
        <WebhookSecretCard
          settings={current}
          canEdit={canEditSettings(scope)}
          canSee={canSeeSecrets(scope)}
          viewAs={scope.readOnlyReason === 'view-as'}
          actions={actions}
        />
      </React.Fragment>
    )
  }

  function renderProfile() {
    if (profile.isLoading) return <LoadingState />
    if (profile.isError || !profile.data) {
      return <ErrorState message={apiErrorMessage(profile.error, 'Could not load the organization.')} />
    }
    return <OrganizationSection profile={profile.data} canEdit={canEditProfile(scope)} actions={actions} />
  }

  return (
    <>
      <PageHeader title="Settings" description="How PixelSign reaches your systems, and how signers see you." />
      <ReadOnlyBanner />
      <div className="max-w-3xl space-y-8">
        <SettingsSection
          title="Integration"
          appliesTo={scope.mode}
          description={`Separate for live and test. Switch to ${scope.mode === 'live' ? 'Test' : 'Live'} to change the ${scope.mode === 'live' ? 'test' : 'live'} ones.`}
        >
          {renderIntegration()}
        </SettingsSection>
        <SettingsSection
          title="Organization profile"
          appliesTo="both"
          description="Shared by live and test, so test emails and signing pages look exactly like live ones. A change applies to both right away."
        >
          {renderProfile()}
        </SettingsSection>
        {mcpEnabled ? (
          <SettingsSection title="AI apps (MCP)" appliesTo="both" description="What AI apps connected to PixelSign can do in this organization.">
            <McpSettingCard canEdit={canManageTeam(scope)} />
          </SettingsSection>
        ) : null}
      </div>
    </>
  )
}
